import React, { useState, useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import PropTypes from 'prop-types';
import { Button } from '@uhg-abyss/web/ui/Button';
import { RenderHtml } from '@src/common/RenderHtml';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Brandmark } from '@uhg-abyss/web/ui/Brandmark';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { useGetTermsOfUseQuery } from '@sde/widgets-core/src/common/hooks/useGetTermsOfUseQuery';
import { useUpdateTermsOfUseQuery } from '@sde/widgets-core/src/common/hooks/useUpdateTermsOfUseQuery';
import { ErrorModal } from './ErrorModal';

const TitleSubText1 = styled('div', {
  padding: '7px 0 15px',
});

const TitleSubText2 = styled('div', {
  width: '600px',
});

const StyledModal = styled(Modal, {
  '&.abyss-modal-overlay': {
    // adding one more 9 here to be layed on top of provider drawer
    zIndex: 9999999,
  },
});

export const TermsOfUseModal = ({ setCanShowTrainingTips }) => {
  const { getHomePageContentByKey, getMessage } = usePPDLagoon();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTouError, setIsTouError] = useState(false);
  const [isChecked, setChecked] = useState(false);
  const { user } = useUser.state();
  const [getTermsOfUseResult, getTermsOfUse] = useGetTermsOfUseQuery({
    onCompleted: (result) => {
      if (result) {
        setIsModalOpen(!result.accepted);
        if (result.accepted) {
          setCanShowTrainingTips(true);
        }
      }
    },
    onError: (error) => {
      if (error) {
        setIsModalOpen(true);
      }
    },
  });

  const [, updateTermsOfUse] = useUpdateTermsOfUseQuery({
    onCompleted: () => {
      setIsModalOpen(false);
    },
    onError: (error) => {
      if (error) {
        setIsModalOpen(false);
        setIsTouError(true);
      }
    },
  });

  const { isTechnicalUser } = user;
  const titleSubtext1Message = getMessage('terms-of-use-modal-title-subtext1');
  const titleSubtext2Message = getMessage('terms-of-use-modal-title-subtext2');

  const touPdf = getHomePageContentByKey('TERMSOFUSEPDF');
  const privacyInfo = getHomePageContentByKey('PRIVACYPOLICYPDF');

  const onClose = () => {
    setIsModalOpen(false);
  };

  const onAccept = () => {
    setCanShowTrainingTips(true);
    updateTermsOfUse({
      variables: {
        uuid: user.uuid,
        accepted: !getTermsOfUseResult?.data?.accepted,
      },
    });
  };

  useEffect(() => {
    if (user?.uuid) {
      getTermsOfUse({
        variables: { uuid: user.uuid },
      });
    }
  }, [user?.uuid]);

  const getLabel = () => {
    return (
      <Text>
        By selecting this checkbox, I have reviewed and agree to the &nbsp;
        <Link fontWeight='$bold' href={touPdf.href} hideIcon>
          {touPdf.name}
        </Link>
        &nbsp; and the &nbsp;
        <Link fontWeight='$bold' href={privacyInfo.href} hideIcon>
          {privacyInfo.name} Policy.
        </Link>
      </Text>
    );
  };

  return (
    <Choose>
      <When condition={isTouError}>
        <ErrorModal isOpen={isTouError} />
      </When>
      <Otherwise>
        <StyledModal
          data-testid='terms-of-use-modal'
          isOpen={isModalOpen}
          width={800}
          onClose={onClose}
          hideClose={!isTechnicalUser}
          closeOnEscPress={isTechnicalUser}
          closeOnClickOutside={isTechnicalUser}
        >
          <Modal.Section>
            <Brandmark
              affiliate='uhc'
              variant='lockup'
              color='blue'
              brand='uhc'
              size='135px'
            />
            <Heading offset={1} textAlign='left'>
              Terms of Use and Privacy Policy
            </Heading>
            <TitleSubText1>
              <RenderHtml>{titleSubtext1Message}</RenderHtml>
            </TitleSubText1>
            <TitleSubText2>
              <RenderHtml>{titleSubtext2Message}</RenderHtml>
            </TitleSubText2>
            <Checkbox
              data-testid='terms-of-use-modal-checkbox'
              css={{
                'abyss-checkbox-root': {
                  padding: '20px 0 20px 0',
                },
              }}
              label={getLabel()}
              size='$sm'
              isChecked={isChecked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <Button
              data-testid='terms-of-use-modal-button'
              onClick={onAccept}
              isDisabled={!isChecked || isTechnicalUser}
            >
              Accept
            </Button>
          </Modal.Section>
        </StyledModal>
      </Otherwise>
    </Choose>
  );
};

TermsOfUseModal.propTypes = {
  setCanShowTrainingTips: PropTypes.func.isRequired,
};
